export const UNSUPPORTED_IMAGE_FORMAT_MESSAGE =
  "This photo format isn't supported. Try another photo or save it as JPEG.";

const HEIC_MIME = new Set([
  'image/heic',
  'image/heif',
  'image/heic-sequence',
  'image/heif-sequence',
]);

const HEIC_BRANDS = new Set([
  'heic',
  'heix',
  'hevc',
  'hevx',
  'heim',
  'heis',
  'hevm',
  'hevs',
  'mif1',
  'msf1',
]);

const MAX_EDGE = 2560;
const JPEG_QUALITY = 0.85;
const JPEG_QUALITY_FLOOR = 0.72;
const SKIP_UNDER_BYTES = 400 * 1024;
const TARGET_MAX_BYTES = 1.5 * 1024 * 1024;

type ImageKind = 'skip' | 'heic' | 'png' | 'webp' | 'jpeg' | 'raster';

function endsWith(name: string, ext: string): boolean {
  return name.toLowerCase().endsWith(ext);
}

function withExt(name: string, ext: string): string {
  const base = name.replace(/\.[^/.]+$/, '').trim();
  return `${base || 'image'}.${ext}`;
}

async function imageKind(file: File): Promise<ImageKind> {
  if (file.type.startsWith('video/') || file.type.startsWith('audio/')) return 'skip';
  if (file.type === 'application/pdf' || endsWith(file.name, '.pdf')) return 'skip';
  if (file.type === 'image/svg+xml' || endsWith(file.name, '.svg')) return 'skip';
  if (
    HEIC_MIME.has(file.type.toLowerCase()) ||
    endsWith(file.name, '.heic') ||
    endsWith(file.name, '.heif')
  ) {
    return 'heic';
  }

  const header = new Uint8Array(await file.slice(0, 12).arrayBuffer());
  if (header.length >= 12) {
    const box = String.fromCharCode(header[4]!, header[5]!, header[6]!, header[7]!);
    const brand = String.fromCharCode(header[8]!, header[9]!, header[10]!, header[11]!)
      .replace(/\0/g, ' ')
      .trim();
    if (box === 'ftyp' && HEIC_BRANDS.has(brand)) return 'heic';
  }

  if (
    file.type === 'image/gif' ||
    endsWith(file.name, '.gif') ||
    String.fromCharCode(...header.slice(0, 3)) === 'GIF'
  ) {
    return 'skip';
  }
  if (
    file.type === 'image/png' ||
    endsWith(file.name, '.png') ||
    (header[0] === 0x89 && header[1] === 0x50 && header[2] === 0x4e && header[3] === 0x47)
  ) {
    return 'png';
  }
  if (
    file.type === 'image/webp' ||
    endsWith(file.name, '.webp') ||
    (String.fromCharCode(...header.slice(0, 4)) === 'RIFF' &&
      String.fromCharCode(...header.slice(8, 12)) === 'WEBP')
  ) {
    return 'webp';
  }
  if (
    file.type === 'image/jpeg' ||
    file.type === 'image/jpg' ||
    endsWith(file.name, '.jpg') ||
    endsWith(file.name, '.jpeg') ||
    (header[0] === 0xff && header[1] === 0xd8 && header[2] === 0xff)
  ) {
    return 'jpeg';
  }
  return file.type.startsWith('image/') ? 'raster' : 'skip';
}

function toBlob(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

async function decodeBitmap(file: File, heic: boolean): Promise<ImageBitmap | null> {
  try {
    const bitmap = await createImageBitmap(file);
    if (bitmap.width >= 1 && bitmap.height >= 1) return bitmap;
    bitmap.close();
  } catch {
    /* native decode unavailable */
  }
  if (!heic) return null;

  try {
    const { heicTo } = await import('heic-to');
    const bitmap = await heicTo({ blob: file, type: 'bitmap' });
    if (bitmap.width >= 1 && bitmap.height >= 1) return bitmap;
    bitmap.close();
  } catch {
    return null;
  }

  return null;
}

export async function prepareImageFile(file: File): Promise<File> {
  const kind = await imageKind(file);
  if (kind === 'skip') return file;
  if (kind !== 'heic' && file.size <= SKIP_UNDER_BYTES) return file;

  const heic = kind === 'heic';
  const bitmap = await decodeBitmap(file, heic);
  if (!bitmap) {
    if (heic) throw new Error(UNSUPPORTED_IMAGE_FORMAT_MESSAGE);
    return file;
  }

  const edge = Math.max(bitmap.width, bitmap.height);
  const needsScale = edge > MAX_EDGE;
  const ratio = needsScale ? MAX_EDGE / edge : 1;
  const width = Math.max(1, Math.round(bitmap.width * ratio));
  const height = Math.max(1, Math.round(bitmap.height * ratio));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    bitmap.close();
    if (heic) throw new Error(UNSUPPORTED_IMAGE_FORMAT_MESSAGE);
    return file;
  }
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  const mayHaveAlpha = kind === 'png' || kind === 'webp';
  if (mayHaveAlpha) {
    const pixels = ctx.getImageData(0, 0, width, height).data;
    let hasAlpha = false;
    for (let i = 3; i < pixels.length; i += 4) {
      if (pixels[i]! < 255) {
        hasAlpha = true;
        break;
      }
    }
    if (hasAlpha) {
      if (!needsScale) return file;
      const type = kind === 'webp' ? 'image/webp' : 'image/png';
      const blob = await toBlob(canvas, type, 0.92);
      canvas.width = 1;
      canvas.height = 1;
      if (blob && blob.size > 0 && blob.size < file.size) {
        return new File([blob], withExt(file.name, kind === 'webp' ? 'webp' : 'png'), { type });
      }
      return file;
    }
  }

  let quality = JPEG_QUALITY;
  let jpeg = await toBlob(canvas, 'image/jpeg', quality);
  while (jpeg && jpeg.size > TARGET_MAX_BYTES && quality > JPEG_QUALITY_FLOOR) {
    quality = Math.max(JPEG_QUALITY_FLOOR, quality - 0.05);
    jpeg = await toBlob(canvas, 'image/jpeg', quality);
  }
  canvas.width = 1;
  canvas.height = 1;

  if (!jpeg || jpeg.size === 0) {
    if (heic) throw new Error(UNSUPPORTED_IMAGE_FORMAT_MESSAGE);
    return file;
  }
  if (!heic && !needsScale && jpeg.size >= file.size * 0.95) return file;
  return new File([jpeg], withExt(file.name, 'jpg'), { type: 'image/jpeg' });
}

export async function prepareImageFiles(files: File[]): Promise<File[]> {
  return Promise.all(files.map((file) => prepareImageFile(file)));
}

export function readBlobAsDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === 'string' && result.length > 0) {
        resolve(result);
        return;
      }
      reject(new Error(UNSUPPORTED_IMAGE_FORMAT_MESSAGE));
    };
    reader.onerror = () => reject(new Error(UNSUPPORTED_IMAGE_FORMAT_MESSAGE));
    reader.readAsDataURL(blob);
  });
}
